"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/hooks/use-session";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, Heart, LogOut, Shield, Tags } from "lucide-react";
import { authClient } from "@/lib/auth-client";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function AuthButtons() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { session, isPending } = useSession();

  if (isPending) {
    return <div aria-hidden className="h-8 w-24 rounded-full shimmer" />;
  }

  if (!session) {
    return (
      <div className="flex items-center gap-1.5">
        <Button variant="ghost" size="sm" asChild>
          <Link href="/login">Log in</Link>
        </Button>
        <Button variant="primary" size="sm" asChild className="rounded-full px-3.5">
          <Link href="/register">Sign up</Link>
        </Button>
      </div>
    );
  }

  const user = session.user as { name: string; email?: string; role?: string };
  const isAdmin = user.role === "admin";
  const isModerator = user.role === "moderator";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex h-9 items-center gap-2 rounded-full border border-transparent pr-2 pl-1 text-sm text-ink-2 transition-colors hover:border-line hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-accent data-[state=open]:border-line data-[state=open]:bg-surface-2"
          aria-label={`Account menu for ${user.name}`}
        >
          <Avatar className="size-7">
            <AvatarFallback className="text-[11px]">{initials(user.name)}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-32 truncate sm:inline">{user.name}</span>
          <ChevronDown className="hidden size-3.5 text-ink-3 sm:inline" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="normal-case tracking-normal">
          <span className="block truncate text-[13px] font-medium text-ink">{user.name}</span>
          {user.email && (
            <span className="block truncate text-[11.5px] font-normal text-ink-3">
              {user.email}
            </span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href="/favorites">
            <Heart />
            Favorites
          </Link>
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link href="/admin">
              <Shield />
              Admin
            </Link>
          </DropdownMenuItem>
        )}
        {(isAdmin || isModerator) && (
          <DropdownMenuItem asChild>
            <Link href="/moderation/tags">
              <Tags />
              Moderation
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            await authClient.signOut();
            queryClient.setQueryData(["session"], null);
            queryClient.setQueryData(["favorites"], []);
            router.push("/");
            router.refresh();
          }}
        >
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
